/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import React, { ReactNode, useRef } from 'react';
import FlagIcon from '@mui/icons-material/Flag';

import styles from './FhirWidgetPanel.module.scss';
import { getCpt4Code, getDiagnosticReportText, getLoincCode, isProblematicCPTCode } from './fhir_utils';
import { IDiagnosticReportSimplified } from './fhir_types';
// Include icon_healthequity.svg
import icon_healthequity from '../public_editorcontent/icon_healthequity.svg';
import FHIRAccordion from './FHIRAccordion';
import { CareEverywhereLogo } from './CareEverywhere/CareEverywhereLogo';
import clsx from 'clsx';
import { useAppSelector } from '../../../app/hooks';
import { selectCreatorMode } from '../chat/chatSlice';

interface FhirWidgetPanelProps {
  children: React.ReactNode;
  diagnosticReport?: IDiagnosticReportSimplified;
  showCode?: boolean;
  showGFRDisclaimer?: boolean;
  onClickTitle?: () => void;
  onClick?: () => Promise<any>;
  color?: string;
  compact?: boolean;
  overrideTitle?: string;
  titleNote?: string; // Suffix to show in parens after title
  onUpdate?: () => void;
  hideAccordionActions?: boolean;
  isWip?: boolean;
  isDeleted?: boolean;
}

export function FhirWidgetPanel({
  color,
  children,
  diagnosticReport,
  onClickTitle,
  overrideTitle, // deprecated
  titleNote,
  showGFRDisclaimer,
  showCode,
  onClick,
  onUpdate,
  compact,
  hideAccordionActions,
  isWip,
  isDeleted,
}: FhirWidgetPanelProps) {
  const [loadingObservations, setLoadingObservations] = React.useState(false);
  const isCreatorMode = useAppSelector(selectCreatorMode);

  const code = getCpt4Code(diagnosticReport?.code?.coding);
  const loincCode = getLoincCode(diagnosticReport?.code?.coding);
  const { text: diagnosticCodeText, isDiagnosticReportTextRedlisted } = getDiagnosticReportText(diagnosticReport?.code); // Will normally be diagnosticReport?.code?.text, unless blacklisted
  async function handleClick(event: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    if (!onClick) return;
    event.stopPropagation();
    setLoadingObservations(true);
    await onClick();
    setLoadingObservations(false);
  }
  let codeJsx: JSX.Element | null = null;
  if (showCode && (code || loincCode)) {
    let prob = null;
    if (code && isProblematicCPTCode(code)) {
      prob = (
        <span title="This CPT code is problematic because it's known to be used for multiple different tests.">
          &nbsp;
          <FlagIcon color="error" fontSize="inherit" />
        </span>
      );
    }
    codeJsx = (
      <>
        {code && (
          <span className={styles.code}>
            {' '}
            [{code}
            {prob}]
          </span>
        )}
        {loincCode && <span className={styles.loincCode}> [Loinc: {loincCode}]</span>}
      </>
    );
  } else {
    // No code to show - add a warning to the user if it is redlisted
    if (isDiagnosticReportTextRedlisted) {
      codeJsx = (
        <span className={styles.code} title="This is a generic name and may be an unrelated test">
          &nbsp;
          <FlagIcon color="error" fontSize="inherit" />
        </span>
      );
    }
  }
  const divRef = useRef<HTMLDivElement>(null);

  const PanelContent = (titleContent?: ReactNode) => {
    if (!children && !titleContent && !loadingObservations) return null; // Don't show an empty panel if there's no content and no title
    return (
      <div
        className={`${styles.container} ${
          loadingObservations ? styles.panelLoading : onClick ? styles.panelCanClickToLoad : ''
        } ${compact ? styles.compact : ''}`}
      >
        {/* Panel should contain title followed by dates and observations */}
        <div
          className={clsx(styles.panel, 'fhir-border')}
          onClick={handleClick}
          style={{ borderColor: color || 'white' }}
          ref={divRef}
        >
          <div className={clsx(styles.titleDateContainer)} onClick={onClickTitle}>
            {!!titleContent && <div className={styles.title}>{titleContent}</div>}
            {showGFRDisclaimer && (
              <div className={styles.GFRDisclaimerContainer}>
                <img src={icon_healthequity} alt="Health Equity" />
                <div className={styles.GFRDisclaimer}>
                  Reported non-African American GFR results represent all race backgrounds
                </div>
              </div>
            )}
          </div>
          {children}
        </div>
      </div>
    );
  };
  const isCareEverywhere = diagnosticReport?.isCareEverywhere;
  const headingText = (
    <>
      {isCreatorMode && isDeleted ? '🗑️❗ ' : null}
      {isCreatorMode && isWip ? '🚧 ' : null}
      {isCareEverywhere ? (
        <>
          <CareEverywhereLogo />
          &nbsp;
        </>
      ) : (
        ''
      )}
      {(!isDiagnosticReportTextRedlisted && overrideTitle?.trim()) || diagnosticCodeText}
      {titleNote ? <> ({titleNote})</> : null}
      {codeJsx}
    </>
  );

  const hasHeadingContent = !!overrideTitle?.trim() || !!diagnosticCodeText || !!isCareEverywhere;

  // else if it is three pane view, then return panel inside accordion.
  return hasHeadingContent ? (
    <FHIRAccordion
      heading={headingText}
      color={color}
      onUpdate={onUpdate}
      divRef={divRef}
      hideActions={hideAccordionActions}
    >
      {PanelContent()}
    </FHIRAccordion>
  ) : (
    PanelContent()
  );
}
